import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { articles } from '../../../data/articles';
import type { Article } from '../../../data/articles';
import ArticleCard from './ArticleCard';

interface RelatedArticlesCarouselProps {
  currentArticleId: string;
}

const RelatedArticlesCarousel: React.FC<RelatedArticlesCarouselProps> = ({ currentArticleId }) => {
  // Hooks devem ser chamados primeiro, antes de qualquer return condicional
  const [currentSlide, setCurrentSlide] = useState(0);

  const currentArticle = articles.find(a => a.id === currentArticleId);
  
  // Buscar artigos relacionados
  const relatedArticles = articles
    .filter(a => 
      a.id !== currentArticleId && 
      (a.category === currentArticle?.category || 
       a.tags.some(tag => currentArticle?.tags.includes(tag)))
    )
    .slice(0, 9); // Máximo 9 artigos (3 slides de 3)

  // Agrupar em slides de 3
  const slides: Article[][] = [];
  for (let i = 0; i < relatedArticles.length; i += 3) {
    slides.push(relatedArticles.slice(i, i + 3));
  }

  const next = () => {
    if (slides.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }
  };

  const prev = () => {
    if (slides.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  };

  if (relatedArticles.length === 0 || slides.length === 0) return null;

  return (
    <section className="py-8 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Outros Artigos</h2>
          {slides.length > 1 && (
            <div className="flex items-center gap-3">
              <button
                onClick={prev}
                className="w-9 h-9 rounded-full bg-white border border-gray-300 hover:bg-gray-50 flex items-center justify-center transition-colors shadow-sm"
                aria-label="Artigo anterior"
              >
                <i className="ri-arrow-left-line text-gray-700 text-sm"></i>
              </button>
              <span className="text-xs text-gray-600 min-w-[50px] text-center">
                {currentSlide + 1} / {slides.length}
              </span>
              <button
                onClick={next}
                className="w-9 h-9 rounded-full bg-white border border-gray-300 hover:bg-gray-50 flex items-center justify-center transition-colors shadow-sm"
                aria-label="Próximo artigo"
              >
                <i className="ri-arrow-right-line text-gray-700 text-sm"></i>
              </button>
            </div>
          )}
        </div>

        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {slides.map((slideArticles, slideIndex) => (
              <div
                key={slideIndex}
                className="min-w-full"
              >
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {slideArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Indicadores de slide */}
        {slides.length > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentSlide
                    ? 'bg-blue-600 w-8'
                    : 'bg-gray-300 hover:bg-gray-400 w-2'
                }`}
                aria-label={`Ir para slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Link para ver todos os artigos */}
        <div className="text-center mt-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm"
          >
            <span>Ver todos os artigos</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RelatedArticlesCarousel;

